/**
 * Centralized Query Key Patterns for React Query
 * 
 * This file defines all query key patterns used throughout the application.
 * Having a centralized location helps prevent typos, improves maintainability,
 * and provides a single source of truth for query key conventions.
 * 
 * Query Key Conventions:
 * - Generated API queries: Use REST-like paths (e.g., '/firms/{firmId}/jobs')
 * - Custom queries: Use array format with namespace first (e.g., ['jobs', firmId])
 * - Legacy queries: Maintained for backwards compatibility
 */

/**
 * Generated API query key patterns
 * These match the auto-generated query keys from the API client
 */
export const ApiQueryKeys = {
  // User profile and authentication
  profile: () => ['/profile'] as const,
  
  // Firm management
  accessibleFirms: (params?: Record<string, unknown>) => 
    ['/firms', ...(params ? [params] : [])] as const,
  firm: (firmId: string) => [`/firms/${firmId}`] as const,
  firmUsers: (firmId: string, params?: Record<string, unknown>) =>
    [`/firms/${firmId}/users`, ...(params ? [params] : [])] as const,
  
  // Job management
  firmJobs: (firmId: string, params?: Record<string, unknown>) =>
    [`/firms/${firmId}/jobs`, ...(params ? [params] : [])] as const,
  job: (firmId: string, jobId: string) => 
    [`/firms/${firmId}/jobs/${jobId}`] as const,
  jobProcessingResults: (firmId: string, jobId: string) =>
    [`/firms/${firmId}/jobs/${jobId}/processing-results`] as const,
  jobProcessingResultsDownload: (firmId: string, jobId: string, params?: Record<string, unknown>) =>
    [`/firms/${firmId}/jobs/${jobId}/processing-results/download`, ...(params ? [params] : [])] as const,
  jobFilesDownload: (firmId: string, jobId: string, params?: Record<string, unknown>) =>
    [`/firms/${firmId}/jobs/${jobId}/files/download`, ...(params ? [params] : [])] as const,
  
  // File management
  filePreview: (params?: Record<string, unknown>) =>
    [`/files/preview`, ...(params ? [params] : [])] as const,
  
  // Operations (admin/reviewer endpoints)
  opsJobs: (params?: Record<string, unknown>) =>
    [`/ops/jobs`, ...(params ? [params] : [])] as const,
  opsMyJobs: (params?: Record<string, unknown>) =>
    [`/ops/my-jobs`, ...(params ? [params] : [])] as const,
  opsJobProcessingResults: (jobId: string) =>
    [`/ops/jobs/${jobId}/processing-results`] as const,
  opsUsers: (params?: Record<string, unknown>) =>
    [`/ops/users`, ...(params ? [params] : [])] as const,
  
  // Audit trail
  audit: (params?: Record<string, unknown>) =>
    [`/audit`, ...(params ? [params] : [])] as const,
} as const;

/**
 * Custom query key patterns
 * Used for application-specific queries that don't directly map to API endpoints
 */
export const CustomQueryKeys = {
  // Legacy job queries (maintained for backwards compatibility)
  jobs: (firmId: string) => ['jobs', firmId] as const,
  jobProcessingResults: (jobId: string) => ['jobProcessingResults', jobId] as const,
  
  // Job detail queries with specific scoping
  job: (firmId: string, jobId: string) => ['job', firmId, jobId] as const,
  jobProcessingResultsOps: (jobId: string) => ['jobProcessingResults', 'ops', jobId] as const,
  jobProcessingResultsClient: (firmId: string, jobId: string) => ['jobProcessingResults', 'client', firmId, jobId] as const,
  
  // Operations-specific queries
  opsJobs: (params?: Record<string, unknown>) => ['ops-jobs', params] as const,
  opsJobsQueue: (params?: Record<string, unknown>) => ['ops-jobs-queue', params] as const,
  
  // Legacy direct job paths (for compatibility)
  legacyJobPath: (firmId: string, jobId: string) => [`/jobs/${firmId}/${jobId}`] as const,
  
  // Application-specific queries
  accessibleFirms: (userId?: string) => ['accessible-firms', userId] as const,
  opsStats: () => ['opsStats'] as const,
  auditTrail: (firmId?: string, page?: number, limit?: number, action?: string, resourceType?: string, pageToken?: string) =>
    ['auditTrail', firmId, page, limit, action, resourceType, pageToken] as const,
} as const;

/**
 * Query key matchers for cache invalidation
 * These functions help identify which queries should be invalidated
 */
export const QueryKeyMatchers = {
  /**
   * Checks if a query key is job-related
   * Comprehensive matcher that covers all job-related patterns used in the app
   */
  isJobRelated: (queryKey: readonly unknown[]): boolean => {
    const firstKey = queryKey[0];
    
    if (typeof firstKey === 'string') {
      // Generated API patterns
      if (firstKey.startsWith('/firms/') && firstKey.includes('/jobs')) {
        return true;
      }
      
      // Operations API patterns
      if (firstKey.startsWith('/ops/') && (
        firstKey.includes('jobs') || 
        firstKey.includes('my-jobs')
      )) {
        return true;
      }
      
      // Legacy direct job paths (e.g., ['/jobs/firmId/jobId'])
      if (firstKey.startsWith('/jobs/')) {
        return true;
      }
      
      // Custom query patterns
      if (firstKey === 'jobs' || 
          firstKey === 'job' || 
          firstKey === 'jobProcessingResults' ||
          firstKey === 'ops-jobs' ||
          firstKey === 'ops-jobs-queue') {
        return true;
      }
    }
    
    // Handle structured query objects anywhere in the array
    return queryKey.some(key => {
      if (typeof key === 'object' && key !== null && !Array.isArray(key)) {
        // Check if any property name in the object contains "job"
        return Object.keys(key).some(k => k.toLowerCase().includes('job'));
      }
      return false;
    });
  },

  /**
   * Checks if a query key is firm-related
   */
  isFirmRelated: (queryKey: readonly unknown[], firmId: string): boolean => {
    return queryKey.some(key => {
      if (typeof key === 'string') {
        return key.startsWith(`/firms/${firmId}`) ||
               key === `/firms/${firmId}`;
      }
      return false;
    });
  },

  /**
   * Checks if a query key is operations-related
   */
  isOpsRelated: (queryKey: readonly unknown[]): boolean => {
    return queryKey.some(key => {
      if (typeof key === 'string') {
        return key.startsWith('/ops/jobs') ||
               key.startsWith('/ops/my-jobs') ||
               key === 'opsStats';
      }
      return false;
    });
  },
} as const;

/**
 * Development utilities for query key debugging
 */
export const QueryKeyUtils = {
  /**
   * Pretty-prints a query key for debugging
   */
  formatQueryKey: (queryKey: readonly unknown[]): string => {
    return queryKey.map(key => {
      if (typeof key === 'string') {
        return key;
      }
      if (typeof key === 'object' && key !== null) {
        return JSON.stringify(key);
      }
      return String(key);
    }).join(' → ');
  },

  /**
   * Gets a human-readable description of query key type
   */
  getQueryKeyType: (queryKey: readonly unknown[]): string => {
    if (QueryKeyMatchers.isJobRelated(queryKey)) {
      return 'Job-related';
    }
    if (QueryKeyMatchers.isOpsRelated(queryKey)) {
      return 'Operations';
    }
    const firstKey = queryKey[0];
    if (typeof firstKey === 'string' && firstKey.startsWith('/firms/')) {
      return 'Firm-related';
    }
    return 'Other';
  },
} as const;

/**
 * Re-export commonly used patterns for convenience
 */
export { ApiQueryKeys as api, CustomQueryKeys as custom, QueryKeyMatchers as matchers };